import React, {useState, useCallback, useMemo} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import {PortalSettingsGetSchema} from '../../../../api/DBSchema/PortalSettings';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import SMCButton from '../../../../components/Simple/SMCButton';
import Sepperator from '../../../../components/Simple/Sepperator';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import TemplateEditorModal from '../../../PopupPages/TemplateEditors/TemplateEditorModal';
import {SMCCheckbox} from '../../../../components/Simple/SMCCheckbox';
import SMCIcon from '../../../../components/Simple/SMCIcon';
import CompanyDetailsEditorModal from '../../../PopupPages/CompanyDetailsEditorModal';
import {isUserAdminSelector} from '../../../../store/selectors/user.selectors';

const pageSchema = PortalSettingsGetSchema.pages.notificationGatewaysReports;
const pageId = pageSchema.pageId;

const ReportsLanguages = {
    english: 'ENGLISH',
    japanese: 'JAPANESE',
    hebrew: 'HEBREW',
    german: 'GERMAN'
}

const templateNames = {
    english: 'client3dreport',
    japanese: 'japanese_client3dreport',
    hebrew: 'hebrew_client3dreport',
    german: 'german_client3dreport'
}

function PortalSettingsGatewayReportsNotifications({
    portalSettingsObject,
    setPortalSettingsData,
    isUserAdmin
}) {
    const { t } = useTranslation();

    const pageData = portalSettingsObject[pageId];
    const setPageValue = useCallback((param, newValue) => {
        setPortalSettingsData([pageId, param], newValue)
    }, [setPortalSettingsData]);

    const englishClient3DReport  = pageData[pageSchema.client3DReport];
    const japaneseClient3DReport = pageData[pageSchema.japaneseClient3DReport];
    const hebrewClient3DReport   = pageData[pageSchema.hebrewClient3DReport];
    const germanClient3DReport   = pageData[pageSchema.germanClient3DReport];
    const companyDetails         = pageData[pageSchema.companyDetails];
    const reportsEditPermission  = pageData[pageSchema.reportsEditPermission];

    const [isCompleteMessageEditorOpen, setIsCompleteMessageEditorOpen] = useState(false);
    const [isPrefaceTextEditorOpen, setIsPrefaceTextEditorOpen] = useState(false);
    const [isCompanyDetailsEditorOpen, setIsCompanyDetailsEditorOpen] = useState(false);

    const [customizeForLanguage, setCustomizeForLanguage] = useState(ReportsLanguages.english);
    const currentlyCustomizedReport = useMemo(() => {
        switch (customizeForLanguage) {
            case ReportsLanguages.english:
                return {
                    object: englishClient3DReport,
                    templateName: templateNames.english,
                    schemaName: pageSchema.client3DReport
                };
            case ReportsLanguages.japanese:
                return {
                    object: japaneseClient3DReport,
                    templateName: templateNames.japanese,
                    schemaName: pageSchema.japaneseClient3DReport
                };
            case ReportsLanguages.hebrew:
                return {
                    object: hebrewClient3DReport,
                    templateName: templateNames.hebrew,
                    schemaName: pageSchema.hebrewClient3DReport
                };
            case ReportsLanguages.german:
                return {
                    object: germanClient3DReport,
                    templateName: templateNames.german,
                    schemaName: pageSchema.germanClient3DReport
                };
            default:
                return null;
        }
    }, [customizeForLanguage, englishClient3DReport, japaneseClient3DReport, hebrewClient3DReport, germanClient3DReport]);

    return (
        <EditorLayout labelWidth={385} lineHeight="32px">
            {/* Language selection */}
            <SMCSelect
                value={customizeForLanguage}
                onChange={e => setCustomizeForLanguage(e.target.value)}
                label={t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.LINE2')}
            >
                <SMCOption value={ReportsLanguages.english}>{t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.ENGLISH')}</SMCOption>
                <SMCOption value={ReportsLanguages.japanese}>{t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.JAPANESE')}</SMCOption>
                <SMCOption value={ReportsLanguages.hebrew}>{t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.HEBREW')}</SMCOption>
                <SMCOption value={ReportsLanguages.german}>{t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.GERMAN')}</SMCOption>
            </SMCSelect>
            {/* Complete message - body editor */}
            <RowLayout>
                <SMCLabel inline useContextWidth>{t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.LINE3')}</SMCLabel>
                <SMCButton onClick={() => setIsCompleteMessageEditorOpen(true)}>
                    <RowLayout spacing="4px"><SMCIcon icon="rightArrow" />{t('SMP.SYSTEM.SETTINGS.NOTIFICATIONS.CUSTOMIZE')}</RowLayout>
                </SMCButton>
            </RowLayout>
            {/* Preface text - short body editor */}
            <RowLayout>
                <SMCLabel inline useContextWidth>{t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.LINE4')}</SMCLabel>
                <SMCButton onClick={() => setIsPrefaceTextEditorOpen(true)}>
                    <RowLayout spacing="4px"><SMCIcon icon="rightArrow" />{t('SMP.SYSTEM.SETTINGS.NOTIFICATIONS.CUSTOMIZE')}</RowLayout>
                </SMCButton>
            </RowLayout>

            <Sepperator />

            {/* Company details */}
            <RowLayout>
                <SMCLabel inline useContextWidth>{t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.COMPANY_DETAILS')}</SMCLabel>
                <SMCButton onClick={() => setIsCompanyDetailsEditorOpen(true)}>
                    <RowLayout spacing="4px"><SMCIcon icon="rightArrow" />{t('SMP.SYSTEM.SETTINGS.NOTIFICATIONS.CUSTOMIZE')}</RowLayout>
                </SMCButton>
            </RowLayout>
            {/* Enable reports settings editing */}
            <SMCCheckbox
                checked={reportsEditPermission}
                onChange={e => setPageValue(pageSchema.reportsEditPermission, e.target.checked)}
                label={t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.LINE5')}
                tooltip={t('SMP.SETTINGS.NOTIFICATIONS.GW_REPORTS.LINE5.TOOLTIP')}
            />

            {/* Popup windows */}
            {/* Body editor */}
            <TemplateEditorModal
                isOpen={isCompleteMessageEditorOpen}
                onRequestClose={() => setIsCompleteMessageEditorOpen(false)}
                defaultObject={currentlyCustomizedReport.object}
                onSuccess={newVal => {
                    setPageValue(currentlyCustomizedReport.schemaName, newVal);
                    setIsCompleteMessageEditorOpen(false);
                }}
                templateName={currentlyCustomizedReport.templateName}
                hasAdministratorsTab={isUserAdmin}
            />
            {/* Short body editor */}
            <TemplateEditorModal
                isOpen={isPrefaceTextEditorOpen}
                onRequestClose={() => setIsPrefaceTextEditorOpen(false)}
                defaultObject={currentlyCustomizedReport.object}
                onSuccess={newVal => {
                    setPageValue(currentlyCustomizedReport.schemaName, newVal)
                    setIsPrefaceTextEditorOpen(false);
                }}
                templateName={currentlyCustomizedReport.templateName}
                editShortBody={true}
                hasAdministratorsTab={isUserAdmin}
            />
            {/* Company details */}
            <CompanyDetailsEditorModal
                isOpen={isCompanyDetailsEditorOpen}
                onRequestClose={() => setIsCompanyDetailsEditorOpen(false)}
                defaultObject={companyDetails}
                onSuccess={newVal => {
                    setPageValue(pageSchema.companyDetails, newVal);
                    setIsCompanyDetailsEditorOpen(false);
                }}
            />
        </EditorLayout>
    )
}

const mapStateToProps = state => ({
    isUserAdmin: isUserAdminSelector(state)
});

const mapDispatchToProps = null;

export default connect(mapStateToProps, mapDispatchToProps)(PortalSettingsGatewayReportsNotifications);